import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatISODate from '../../../Helpers/formatISODate'
import './styles.scss'

const STEPS = [
  { label: 'Shipment Booked',        icon: solid('file-lines') },
  { label: 'Picked Up',              icon: solid('box') },
  { label: 'In Transit',             icon: solid('truck') },
  { label: 'Arrived at Destination', icon: solid('warehouse') },
  { label: 'Delivered',              icon: solid('circle-check') },
]

const getActiveStep = (status) => {
  const s = (status || '').toLowerCase()
  if (s.includes('delivered') || s.includes('complete'))                         return 4
  if (s.includes('arrived') || s.includes('destination') || s.includes('hub'))  return 3
  if (s.includes('transit') || s.includes('shipped') || s.includes('dispatch')) return 2
  if (s.includes('picked') || s.includes('collected'))                           return 1
  return 0
}

const isHaltedStatus = (status) => {
  const s = (status || '').toLowerCase()
  return s.includes('hold') || s.includes('delay') || s.includes('failed') || s.includes('inactive')
}

const getStatusBadge = (status) => {
  const s = (status || '').toLowerCase()
  if (s.includes('delivered') || s.includes('complete'))                         return 'delivered'
  if (s.includes('arrived') || s.includes('destination'))                        return 'out'
  if (s.includes('transit') || s.includes('shipped') || s.includes('dispatch')) return 'transit'
  if (s.includes('hold') || s.includes('delay') || s.includes('failed') || s.includes('inactive')) return 'hold'
  if (s.includes('picked'))                                                       return 'transit'
  return 'processing'
}

const formatDate = (iso) => {
  const raw = formatISODate(iso)
  if (!raw) return '—'
  return raw.split(' ')[0]
}

const InfoCard = ({ title, icon, rows }) => (
  <div className="tir-info-card">
    <div className="tir-info-card__header">
      <FontAwesomeIcon icon={icon} />
      <span>{title}</span>
    </div>
    <div className="tir-info-card__body">
      {rows.map(({ label, value }) => (
        <div key={label} className="tir-info-card__row">
          <span className="tir-info-card__label">{label}</span>
          <span className="tir-info-card__value">{value || '—'}</span>
        </div>
      ))}
    </div>
  </div>
)

const TrackItResult = ({ trackItResult: r, onReset }) => {
  const halted      = isHaltedStatus(r.status)
  const activeStep  = halted ? -1 : getActiveStep(r.status)
  const badgeClass  = getStatusBadge(r.status)

  return (
    <div className="tir">

      {/* ── Header ── */}
      <div className="tir__header">
        <div className="tir__header__left">
          <span className="tir__header__label">Tracking Number</span>
          <h2 className="tir__header__number">{r.trackingNumber}</h2>
        </div>
        <div className="tir__header__right">
          <span className={`tir__badge tir__badge--${badgeClass}`}>
            {r.status}
          </span>
          <button className="tir__reset" onClick={onReset}>
            <FontAwesomeIcon icon={solid('rotate-left')} />
            Track Another
          </button>
        </div>
      </div>

      {halted && (
        <div className="tir__halted-note">
          <FontAwesomeIcon icon={solid('triangle-exclamation')} />
          Tracking progress is currently paused ({r.status}). Please check back later or contact support.
        </div>
      )}

      {/* ── Progress timeline ── */}
      <div className="tir__timeline">
        {STEPS.map((step, i) => {
          const done    = i < activeStep
          const current = i === activeStep
          return (
            <div key={step.label} className={`tir__step${done ? ' done' : ''}${current ? ' current' : ''}`}>
              <div className="tir__step__icon">
                <FontAwesomeIcon icon={step.icon} />
              </div>
              <span className="tir__step__label">{step.label}</span>
              {i < STEPS.length - 1 && <div className={`tir__step__line${done ? ' done' : ''}`} />}
            </div>
          )
        })}
      </div>

      {/* ── Info cards ── */}
      <div className="tir__cards">
        <InfoCard
          title="Sender"
          icon={solid('circle-user')}
          rows={[
            { label: 'Name',    value: r.senderName },
            { label: 'Address', value: r.senderAddress },
          ]}
        />
        <InfoCard
          title="Recipient"
          icon={solid('location-dot')}
          rows={[
            { label: 'Name',    value: r.receiverName },
            { label: 'Address', value: r.receiverAddress },
          ]}
        />
      </div>

      {/* ── Package details ── */}
      <div className="tir-detail-card">
        <div className="tir-detail-card__header">
          <FontAwesomeIcon icon={solid('box-open')} />
          <span>Package Details</span>
        </div>
        <div className="tir-detail-card__grid">
          <div className="tir-detail-card__item">
            <span className="tir-detail-card__item__label">Description</span>
            <span className="tir-detail-card__item__value">{r.packageDescription || '—'}</span>
          </div>
          <div className="tir-detail-card__item">
            <span className="tir-detail-card__item__label">Current Location</span>
            <span className="tir-detail-card__item__value">{r.currentLocation || '—'}</span>
          </div>
          <div className="tir-detail-card__item">
            <span className="tir-detail-card__item__label">Dispatch Date</span>
            <span className="tir-detail-card__item__value">{formatDate(r.dispatchDate)}</span>
          </div>
          <div className="tir-detail-card__item">
            <span className="tir-detail-card__item__label">Estimated Delivery</span>
            <span className="tir-detail-card__item__value eta">{formatDate(r.estimatedDeliveryDate)}</span>
          </div>
        </div>
      </div>

    </div>
  )
}

export default TrackItResult
